import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { isApiError } from '../../api/client';
import { useAcceptInvite, useInvitePreview } from '../../api/hooks';
import { useAuth } from '../../auth/AuthContext';
import { displayName } from '../../lib/next';
import { Button } from '../../ui/Button';
import { IconUsers, LogoMark } from '../../ui/icons';
import { EmptyState, Skeleton } from '../../ui/Misc';
import { ThemeToggle, TopBar } from '../../ui/TopBar';
import { toast } from '../../ui/toast';

export function JoinScreen() {
  const { token = '' } = useParams();
  const { user } = useAuth();
  const preview = useInvitePreview(token);
  const accept = useAcceptInvite(token);
  const navigate = useNavigate();

  if (preview.data?.alreadyMember && !accept.isPending) {
    return <Navigate to={`/lists/${preview.data.listId}`} replace />;
  }

  function onAccept() {
    accept.mutate(undefined, {
      onSuccess: (list) => {
        toast.success(`You joined “${list.title}”`);
        navigate(`/lists/${list.id}`, { replace: true });
      },
    });
  }

  return (
    <div className="min-h-dvh">
      <TopBar
        title={
          <Link to="/" className="inline-flex min-h-11 items-center gap-2.5">
            <LogoMark size={28} />
            <span className="text-lg font-bold tracking-tight">Cady</span>
          </Link>
        }
        right={<ThemeToggle />}
      />
      <main className="mx-auto flex w-full max-w-sm flex-col px-5 pt-10 pb-[calc(var(--safe-bottom)+2rem)]">
        {preview.isPending ? (
          <div className="space-y-4" aria-busy="true" aria-label="Loading invite">
            <Skeleton className="mx-auto size-16 rounded-2xl" />
            <Skeleton className="mx-auto h-7 w-48" />
            <Skeleton className="h-12" />
          </div>
        ) : preview.isError ? (
          <EmptyState
            icon={<IconUsers size={30} />}
            title={isApiError(preview.error) && preview.error.status === 404 ? 'Invite not valid' : 'Couldn’t open invite'}
            action={
              <Link to="/" className="font-semibold text-accent underline-offset-4 hover:underline">
                Go to your lists
              </Link>
            }
          >
            {isApiError(preview.error) && preview.error.status === 404
              ? 'This invite link has expired or was revoked. Ask for a new one.'
              : preview.error.message}
          </EmptyState>
        ) : (
          <div className="rounded-3xl border border-line bg-surface p-6 text-center shadow-sm">
            <div className="mx-auto mb-4 grid size-16 place-items-center rounded-2xl bg-accent-soft text-accent">
              <IconUsers size={30} />
            </div>
            <p className="text-[0.95rem] text-muted">
              {preview.data.ownerName ? `${preview.data.ownerName} invited you to` : 'You’ve been invited to'}
            </p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight break-words text-ink">{preview.data.title}</h1>
            {user && <p className="mt-3 text-sm text-muted">Joining as {displayName(user)}</p>}
            <Button size="lg" block className="mt-6" loading={accept.isPending} onClick={onAccept}>
              Join list
            </Button>
            <Link to="/" className="mt-3 inline-flex min-h-11 items-center text-[0.95rem] font-medium text-muted">
              Not now
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
